import { Component, computed, inject, signal } from '@angular/core';
import { LvrHeading } from '../../../core/components/heading/heading';
import { TranslocoPipe, TranslocoService } from '@jsverse/transloco';
import { LvrPaddingDirective } from '../../../shared/padding/padding.directive';
import { LvrDefaultActions } from '../../../core/components/default-actions/default.actions';
import { LvrRestoreActions } from '../../../shared/storage/restore-actions';
import { AbstractControl, FormBuilder, ReactiveFormsModule, Validators } from '@angular/forms';
import { maxDateValidator, minDateValidator } from '../../../shared/validators/max-date-validator';
import { SearchResultItem } from '../../../api/app-types/search.app';
import { DocumentFile, DocumentFileSubjectType } from '../../../api/app-types/document.app';
import { allowedFileTypes, maxFiles, maxFileSize } from '../../../shared/document-upload/document-upload-validators';
import { ENVIRONMENT } from '../../../../environments/environment.interface';
import { ContactType } from '../../../api/app-types/contact-type';
import { ImmediateErrorStateMatcher } from '../../../shared/validators/immediate-error-state-matcher';
import { CUSTOM_DATE_FORMATS } from '../../../core/adapters/custom-date-format';
import { MatFormFieldModule } from '@angular/material/form-field';
import { DatePipe } from '@angular/common';
import { MatInputModule } from '@angular/material/input';
import { MatSelectModule } from '@angular/material/select';
import { FORM_KEYS } from '../../../shared/storage/form-keys.type';
import { LvrAutoSaveDirective } from '../../../shared/storage/auto-save.directive';
import { LvrAppendAsteriskPipe } from '../../../shared/pipes/append-asterisk.pipe';
import { LvrField } from '../../../core/components/field/field';
import { LvrInputDirective } from '../../../core/directives/lvr-input.directive';
import { hasError, hasFileError } from '../../../shared/helpers/has-error.func';
import { MatDatepickerModule } from '@angular/material/datepicker';
import { FormPersistenceService } from '../../../shared/storage/form-persistence.service';
import { NOTIFICATION_SERVICE } from '../../../shared/notification/notification';
import { formatDateToISOString, matDateProviders } from '../../../shared/helpers/date.func';
import { PaginatedResponse } from '../../../shared/pagination/paginated-response';
import { compareById } from '../../../shared/helpers/compare-by-id.func';
import { Participants } from '../../contact/participants/participants';
import { DocumentUploadControl } from '../../../shared/document-upload/document-upload';
import { User } from '../../../api/app-types/user.app';
import { MatCheckboxModule } from '@angular/material/checkbox';
import { CreateContactBody } from '../../../api/app-types/contact.app';
import { CONTACT_API } from '../../../api/services/contact.service';
import { createErrorNotification, createSuccessNotification } from '../../../shared/notification/notification.factory';
import { RECORD_MANAGER } from '../../../core/services/record.provide';
import { RouteInjection } from '../../../shared/routing/route-injection.class';
import { SessionManagerService } from '../../../shared/storage/session-manager.service';
import { hasPermission } from '../../../shared/permissions/permission.func';
import { LOGGER } from '../../../shared/logging/log.service';

@Component({
	selector: 'lvr-network-contact-create',
	imports: [
		TranslocoPipe,
		LvrHeading,
		LvrPaddingDirective,
		LvrAppendAsteriskPipe,
		LvrField,
		LvrInputDirective,
		LvrDefaultActions,
		LvrRestoreActions,
		Participants,
		DocumentUploadControl,
		DatePipe,
		ReactiveFormsModule,
		LvrAutoSaveDirective,
		MatFormFieldModule,
		MatInputModule,
		MatDatepickerModule,
		MatCheckboxModule,
		MatSelectModule,
	],
	providers: [matDateProviders],
	templateUrl: './network-contact-create.html',
	styleUrl: './network-contact-create.scss',
})
export class NetworkContactCreate extends RouteInjection {
	private readonly env = inject(ENVIRONMENT);
	private readonly api = inject(CONTACT_API);
	private readonly ntfySvc = inject(NOTIFICATION_SERVICE);
	private readonly draftService = inject(FormPersistenceService);
	private readonly transloco = inject(TranslocoService);
	private readonly recordManager = inject(RECORD_MANAGER);
	private readonly session = inject(SessionManagerService);
	private readonly logger = inject(LOGGER);

	public readonly user = this.resolveDataAsSignal<User>('user');
	private readonly contactTypesPage = this.resolveDataAsSignal<PaginatedResponse<ContactType>>('contactTypes');
	public readonly contactTypes = computed(() => this.contactTypesPage()?.entities);

	public readonly isLoading = signal<boolean>(false);
	public readonly matcher = new ImmediateErrorStateMatcher();
	public readonly compareById = compareById;
	public readonly draftId = computed(() => FORM_KEYS.CREATE_NETWORK_CONTACT);
	public readonly canCreateContactsPermission = hasPermission(this.session.permissions, 'Contacts.Create');

	public readonly dateRange = {
		min: this.env.formConfiguration.contact.dateWarningRange.min,
		max: this.env.formConfiguration.contact.dateWarningRange.max, // days retroactively
	};
	public readonly dateDisplayFormat = CUSTOM_DATE_FORMATS.display.dateInput;
	public readonly dateFormatHint = this.env.visualDateFormat;
	public readonly remarkCtrlMaxLength = this.env.textareaMaxLength;
	public readonly keywordCtrlMaxLength = this.env.textInputMaxLength;

	public readonly subjectType = DocumentFileSubjectType.CONTACT;
	public readonly maxFileCount = this.env.contactSettings.maxFiles;
	public readonly maxFileSize = this.env.fileSettings.maxFileSize;
	public readonly allowedFileTypes = this.env.fileSettings.allowedMimeTypes;

	public readonly minDate = computed(() => {
		const minusDays = this.dateRange.max; // 42
		if (!minusDays) return undefined;

		const today = new Date();
		const d = new Date(today);

		d.setDate(d.getDate() - minusDays);
		d.setHours(0, 0, 0, 0);

		return d;
	});

	public readonly maxDate = computed(() => {
		const plusDays = this.dateRange.min ?? 0;

		const d = new Date();
		d.setDate(d.getDate() + plusDays);
		d.setHours(23, 59, 59, 999);

		return d;
	});

	public readonly form = this.initForm(inject(FormBuilder));

	public hasError = (control: AbstractControl | null, ...errors: string[]) => hasError(control, ...errors);
	public hasFileError = (control: AbstractControl | null, ...errors: string[]) => hasFileError(control, ...errors);

	public cancel = () => {
		this.draftService.deleteDraft('contact', this.draftId());
		this.recordManager.closeAndSwitchToNextRecord();
	};

	public async save() {
		if (this.isLoading()) return;

		if (this.form.invalid) {
			this.form.markAllAsTouched();
			return;
		}

		const fv = this.form.value;
		const docs = (fv.documents ?? []) as DocumentFile[];

		const body: CreateContactBody = {
			anonym: fv.anonym ?? false,
			confidential: fv.confidential ?? false,
			contactedAt: formatDateToISOString(fv.contactedAt) ?? '',
			contactType: fv.contactType!,
			keyword: fv.keyword?.trim() ?? '',
			remark: fv.remark?.trim() ?? '',
			participants: (fv.participants ?? []).map((p: SearchResultItem) => ({
				id: p.item.id,
				typeId: p.personType,
			})),
			documentIds: docs.map((d: DocumentFile) => d.id),
			selectedTasksIds: [],
		};

		const NO_CLIENT_ID = -1;
		const NO_ASSIGNMENT_ID = -1;

		this.isLoading.set(true);
		try {
			try {
				// Use -1 to indicate 'no client/assignment' for standalone contacts.
				await this.api.create(NO_CLIENT_ID, NO_ASSIGNMENT_ID, body);
			} catch {
				this.ntfySvc.addNotification(
					createErrorNotification(this.transloco.translate('base.notification_message.network_contact_create_error')),
				);
				return;
			}

			this.draftService.deleteDraft('contact', this.draftId());

			const active = this.recordManager.activeRecord();
			let navigationSucceeded = false;
			try {
				navigationSucceeded = await this.router.navigateByUrl('/');
			} catch (error) {
				this.logger.error(error as Error);
			}

			if (navigationSucceeded && active?.id) this.recordManager.closeRecord(active.id);

			this.ntfySvc.addNotification(
				createSuccessNotification(this.transloco.translate('base.notification_message.network_contact_created')),
			);
		} finally {
			this.isLoading.set(false);
		}
	}

	private initForm(fb: FormBuilder) {
		return fb.group({
			anonym: fb.control<boolean>(false),
			remark: fb.control<string>(''),
			confidential: fb.control<boolean>(false),
			keyword: fb.control<string>('', [Validators.required]),
			contactType: fb.control<ContactType | undefined>(undefined, [Validators.required]),
			contactedAt: fb.control<Date | undefined>(undefined, [
				Validators.required,
				minDateValidator(this.minDate()),
				maxDateValidator(this.maxDate()),
			]),
			participants: fb.control<SearchResultItem[]>([]),
			documents: fb.control<DocumentFile[]>(
				[],
				[maxFiles(this.maxFileCount), maxFileSize(this.maxFileSize), allowedFileTypes(this.allowedFileTypes)],
			),
		});
	}
}
